import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App as AntdApp, Button, Empty, Popconfirm } from 'antd';

import { deleteDraft, listDrafts } from '../services/drafts';
import useDateFormat from '../hooks/useDateFormat';
import { formatDisplayDate } from '../utils/dateDisplay';
import { formatRelativeToNow } from '../utils/formatters';
import { normalizeId } from '../utils/ids';
import RecordBadge from '../components/common/RecordBadge';
import { CollectionScope } from '../contexts/CollectionScopeContext';
import ContextBar from '../components/common/ContextBar';
import { destructivePopconfirmProps } from '../components/common/confirmDestructive';
import { FileChip, FilePicture } from '../components/common/FileChip';
import { showsAsPicture } from '../utils/fileValues';
import { DataState, Skeleton, deriveDataState } from '../components/loading';

/** "3 fields" — how much of the form the draft holds. */
function filledText(count) {
	const n = Number(count) || 0;
	if (!n) return 'Nothing filled in yet';
	return `${n} field${n === 1 ? '' : 's'} filled in`;
}

// How many of a draft's files its row shows before "+N".
const DRAFT_FILES_SHOWN = 6;

/**
 * A draft's files (PR 10b, F2 keep): the draft's `files` facts, its own held
 * uploads through the held route (its uploader's only) and an id a Duplicate
 * seeded through its source item when the facts name it. Photos as small
 * thumbnails, anything else as the file chip.
 */
function DraftFiles({ draft }) {
	const files = draft?.files && typeof draft.files === 'object' ? Object.entries(draft.files) : [];
	if (!files.length) return null;
	const heldIn = normalizeId(draft?.collection_id);
	const shown = files.slice(0, DRAFT_FILES_SHOWN).map(([rawId, facts]) => {
		const file = {
			_id: normalizeId(rawId),
			originalName: typeof facts?.name === 'string' ? facts.name : '',
			ext: facts?.ext || '',
			size: Number.isFinite(facts?.size) ? facts.size : null,
			image: facts?.image && typeof facts.image === 'object' ? facts.image : null,
			variants: Array.isArray(facts?.variants) ? facts.variants : [],
		};
		const itemId = normalizeId(facts?.itemId || facts?.subjectId) || null;
		return showsAsPicture(file, 'sm')
			? <FilePicture key={file._id} itemId={itemId} heldIn={itemId ? null : heldIn} file={file} size="sm" chipSize="xs" className="drafts-page__file-picture" />
			: <FileChip key={file._id} file={file} size="xs" />;
	});
	const more = files.length - DRAFT_FILES_SHOWN;
	return (
		<div className="drafts-page__files" data-testid="draft-files">
			{shown}
			{more > 0 ? <span className="drafts-page__files-more">+{more}</span> : null}
		</div>
	);
}

/** Where Open goes: the card's new-item form, with the draft applied on arrival. */
export function draftFormPath(draft) {
	const collectionId = normalizeId(draft?.collection_id);
	const cardId = normalizeId(draft?.card_id);
	const id = normalizeId(draft?._id);
	return `/collections/${collectionId}/items/new/${cardId}?draft=${encodeURIComponent(id)}`;
}

/**
 * Drafts: every new item this person started and left, across collections,
 * newest first (keepr-api SCHEMA.md § Item drafts). Private to them — the
 * server answers with nobody else's. A draft whose card they can still read
 * but no longer add items of (an archived collection, a read grant) stays
 * listed, named, so it can be deleted. A draft of a collection they can no
 * longer READ was deleted by the server on this very read; it shows once
 * (`lost`), with no title or names, so nothing just vanishes unexplained.
 * `?collection=<id>` narrows the list (the collection home's "N drafts"
 * link).
 */
export default function Drafts() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const collectionFilter = normalizeId(searchParams.get('collection')) || '';
	const { message } = AntdApp.useApp();
	const [drafts, setDrafts] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [loading, setLoading] = useState(true);
	const requestRef = useRef(0);
	const [deletingId, setDeletingId] = useState(null);
	useDateFormat();

	// The rows stay on screen while a filter change or a Retry reads again; a
	// failed read leaves them and says so above them (section 14, refreshing).
	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setLoadError(null);
		listDrafts({ collectionId: collectionFilter || undefined, limit: 200 })
			.then(({ drafts: rows }) => { if (request === requestRef.current) { setDrafts(rows); setLoading(false); } })
			.catch(error => { if (request === requestRef.current) { setLoadError(error); setLoading(false); } });
	}, [collectionFilter]);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);

	// `?draft=<id>`: the three-day notice's door (utils/draftNotice). The
	// draft still here opens its form; one saved or deleted since is said.
	const draftParam = normalizeId(searchParams.get('draft')) || '';
	const [draftGone, setDraftGone] = useState(false);
	useEffect(() => {
		if (!draftParam || drafts === null) return;
		const row = drafts.find(draft => normalizeId(draft?._id) === draftParam);
		if (row && !row.lost && row.available !== false) navigate(draftFormPath(row), { replace: true });
		else setDraftGone(true);
	}, [draftParam, drafts, navigate]);

	const filterName = useMemo(() => {
		if (!collectionFilter) return '';
		const row = (drafts || []).find(draft => draft?.collection && normalizeId(draft.collection_id) === collectionFilter);
		return row?.collection?.name || '';
	}, [drafts, collectionFilter]);

	const handleDelete = async (draft) => {
		const id = normalizeId(draft?._id);
		setDeletingId(id);
		try {
			await deleteDraft(id);
			setDrafts(current => (current || []).filter(row => normalizeId(row?._id) !== id));
			message.success('Draft deleted.');
		} catch {
			message.error('Deleting that draft failed. Please try again.');
		} finally {
			setDeletingId(null);
		}
	};

	const renderLostRow = (draft) => (
		<li
			key={normalizeId(draft?._id)}
			className="drafts-page__row drafts-page__row--unavailable"
			data-testid="draft-row"
		>
			<div className="drafts-page__main">
				<div className="drafts-page__identity">
					<span className="drafts-page__title--plain">A draft in a collection you no longer have access to</span>
				</div>
				<div className="drafts-page__meta">Deleted, because you can no longer see that collection.</div>
			</div>
		</li>
	);

	const renderRow = (draft) => {
		if (draft?.lost) return renderLostRow(draft);
		const id = normalizeId(draft?._id);
		const title = String(draft?.title || '').trim() || 'Untitled draft';
		const available = draft?.available !== false;
		// Named when the server named it: available, or readable but not
		// writable. An unnamed unavailable row is from an older API.
		const named = Boolean(draft?.collection && draft?.card);
		const meta = [
			`Saved ${formatRelativeToNow(draft?.updatedAt) || '—'}`,
			filledText(draft?.filled),
			// A draft holding files says when they go with it (`keptUntil`,
			// the draft's own expiry, which its held files share).
			(draft?.keptUntil || draft?.expiresAt) ? `Kept until ${formatDisplayDate(draft.keptUntil || draft.expiresAt)}` : null,
		].filter(Boolean).join(' · ');
		// The door to the draft's collection, outside the pill (the badge is
		// not a link; its collection is a segment of it, never a second one).
		const collectionId = normalizeId(draft?.collection_id);
		const collectionName = typeof draft?.collection?.name === 'string' ? draft.collection.name.trim() : '';
		const openCollection = named && available && collectionId && collectionName ? (
			<Link className="drafts-page__open-collection" to={`/collections/${collectionId}`}>{`Open ${collectionName}`}</Link>
		) : null;
		return (
			<li
				key={id}
				className={`drafts-page__row${available ? '' : ' drafts-page__row--unavailable'}`}
				data-testid="draft-row"
			>
				<div className="drafts-page__main">
					<div className="drafts-page__identity">
						{named ? (
							// One qualified badge: the collection the draft is
							// for, then the card name over its title.
							<RecordBadge
								kind="item"
								record={draft.card}
								kicker={draft.card?.name}
								title={title}
								size="sm"
								collection={{ _id: draft.collection_id, ...draft.collection }}
							/>
						) : (
							<span className="drafts-page__title--plain">{title}</span>
						)}
					</div>
					<div className="drafts-page__meta">
						{available ? meta : 'No longer available: you can no longer add items of this card there.'}
						{openCollection ? <>{' · '}{openCollection}</> : null}
					</div>
					{available ? <DraftFiles draft={draft} /> : null}
				</div>
				<div className="drafts-page__actions">
					{available ? (
						<Button onClick={() => navigate(draftFormPath(draft))}>Open</Button>
					) : null}
					<Popconfirm
						title="Delete this draft?"
						{...destructivePopconfirmProps('Delete')}
						onConfirm={() => handleDelete(draft)}
					>
						<Button danger loading={deletingId === id} aria-label={`Delete draft ${title}`}>
							Delete
						</Button>
					</Popconfirm>
				</div>
			</li>
		);
	};

	return (
		<>
			<ContextBar
				title="Drafts"
				actions={collectionFilter ? (
					<Button onClick={() => setSearchParams({})}>Show all drafts</Button>
				) : null}
			/>
			{collectionFilter && filterName ? (
				<p className="drafts-page__meta">Drafts in {filterName}</p>
			) : null}
			{draftGone ? (
				<Alert className="mb-2" type="info" showIcon message="That draft is no longer here: it was saved as an item or deleted." />
			) : null}

			<DataState
				state={deriveDataState({
					loading,
					error: loadError,
					hasData: drafts !== null,
					isEmpty: drafts !== null && drafts.length === 0,
				})}
				hasData={drafts !== null}
				isEmpty={drafts !== null && drafts.length === 0}
				skeleton={<Skeleton variant="list-row" count={4} />}
				empty={<Empty description="No drafts. A new item you start and leave waits here for 30 days." />}
				error={loadError ? 'Check your connection and try again.' : null}
				onRetry={load}
				label="your drafts"
			>
				{/* Narrowed to one collection, its drafts are at home here:
				    their badges drop the collection they would all repeat. */}
				<CollectionScope collectionId={collectionFilter || null}>
					<ul className="drafts-page__list">{(drafts || []).map(renderRow)}</ul>
				</CollectionScope>
			</DataState>
		</>
	);
}
